/**
 * Drop a session that has been away too long, before anything reads it.
 *
 * THE REQUIREMENT IS OLD AND WAS NEVER IMPLEMENTED. `AppUnloadLogout` has said since it was written
 * that "navigating away from the website or restoring a browser session (crash, force close, etc)
 * should log the user out and not return them back to their previous valid session". It registered
 * an `unload` listener that wrote `appUnloadedAt` to sessionStorage -- and NOTHING anywhere read
 * that key. One writer, zero readers: the second half was never built, so the requirement was a
 * comment rather than a behaviour.
 *
 * AND THE WRITER HAD STOPPED FIRING TOO. Chrome blocks `unload` by permissions policy, which is the
 * console violation Ed brought: "[Violation] Permissions policy violation: unload is not allowed in
 * this document." So by 2026-10-27 the feature was dead at both ends, and because Chrome restores
 * sessionStorage for restored tabs, a crash or force-quit brought the access token back with it.
 *
 * WHY A WINDOW AND NOT "ALWAYS". `pagehide` -- the event that is not blocked -- fires on an ordinary
 * refresh and on any navigation away, not only on the cases the requirement is about. Clearing the
 * session whenever a marker is present would log somebody out every time they pressed reload. The
 * window is what separates "came back a second later" from "came back tomorrow", and it is the half
 * the original was missing rather than a weakening of it.
 */

//: How long the app may be away before the session is dropped. Ed chose a staleness window on
//: 2026-10-27 and five minutes with it: comfortably longer than a reload, a redirect or a slow
//: navigation, and far shorter than stepping away from the machine.
export const STALE_AFTER_MS = 5 * 60 * 1000;

const MARKER = "appUnloadedAt";

/** Record that the page is going away. Call from `pagehide`. */
export function markPageHidden(now: number = Date.now()): void {
    try {
        sessionStorage.setItem(MARKER, String(now));
    } catch {
        // A BLOCKED sessionStorage IS NOT A REASON TO BREAK THE PAGE. Private windows and blocked
        // site data throw here; the cost is that this one protection does not apply, which is the
        // state the whole estate was in until today.
    }
}

/**
 * Decide, and act, on how long the session has been away.
 *
 * Returns true when the session was dropped, so a caller can say so. The marker is cleared either
 * way: a stale one left behind would drop the NEXT session too, on a page that had never been away.
 */
export function clearStaleSession(now: number = Date.now(),
                                  staleAfterMs: number = STALE_AFTER_MS): boolean {
    let marker: string | null = null;
    try {
        marker = sessionStorage.getItem(MARKER);
    } catch {
        return false;
    }
    if (marker === null) return false;

    const hiddenAt = Number(marker);
    // A MARKER THAT IS NOT A NUMBER IS NOT A LICENCE TO STAY. `Number("")` is 0 and `Number("x")`
    // is NaN; treating either as "recent" would make a corrupted marker the way to keep a session
    // alive for ever, so anything unreadable counts as stale.
    const stale = !Number.isFinite(hiddenAt) || (now - hiddenAt) >= staleAfterMs;

    try {
        if (stale) {
            sessionStorage.clear();
        } else {
            sessionStorage.removeItem(MARKER);
        }
    } catch {
        return false;
    }
    return stale;
}
